import { ChevronRight } from 'lucide-react';
import netsuiteLogo from '../../assets/logos/netsuite.svg';
import type { NetSuiteStatus } from '../../../../src/application/sources/netsuite-status.js';
import { IntegrationCard, type Fact } from './IntegrationCard';
import { netsuiteAdminSteps, netsuiteFreshness, netsuiteHealth, relativeTime, withFreshness } from './model';

/**
 * NetSuite, read through Ampersand: Ampersand reads customers and invoices on a schedule and posts them to Alder, which
 * verifies each delivery. The card is drawn from this host's state only (webhook configured, the pinned account, counts).
 * Ampersand's embeddable install screen is not wired yet, so until the account is linked the card lists what the
 * customer's NetSuite admin does, and Alder completes the connection with them.
 */
export function NetSuiteCard({ status }: { status: NetSuiteStatus }) {
  const freshness = status.account ? netsuiteFreshness(status.lastDeliveryAt) : null;
  const health = withFreshness(netsuiteHealth(status), freshness);
  const keyFacts: Fact[] = status.account ? [
    { label: 'Customers', value: status.customers.toLocaleString('en-US') },
    { label: 'Invoices', value: status.invoices.toLocaleString('en-US') },
    ...(status.firstLook.ready ? [{ label: 'First look', value: <a href="#today">First look ready</a> }] : []),
  ] : [];
  const facts: Fact[] = status.account ? [
    { label: 'Account', value: <code>{status.account}</code> },
    { label: 'Last change received', value: status.lastDeliveryAt ? relativeTime(status.lastDeliveryAt) : 'None yet' },
  ] : [{ label: 'Alder side', value: status.webhookConfigured ? 'Ready' : 'Alder is finishing setup' }];
  const summary = status.account ? 'Alder reads customers and invoices from NetSuite.'
    : 'Alder reads customers and invoices from NetSuite, view-only. About 15 minutes for your NetSuite admin.';
  return <IntegrationCard id="netsuite" logo={netsuiteLogo} name="NetSuite" role="ERP · read-only"
    health={health} summary={summary} freshness={freshness} keyFacts={keyFacts} facts={facts}
    reassurance="Read-only: the role you create can only view customers and invoices.">
    {!status.account && <details className="netsuite-steps">
      <summary><ChevronRight size={14} aria-hidden/>How to set this up</summary>
      <ol>{netsuiteAdminSteps.map(step => <li key={step}>{step}</li>)}</ol>
    </details>}
  </IntegrationCard>;
}
